import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchAllHouses } from '../redux/action/house';
import { messages } from '../common/notification';
import { loadHouseForPicker, updateDeviceSchedule } from './api';
import { SCHEDULE_SECTIONS, theme } from './constants';
import {
  buildHouseOptions,
  collectEnergiSyncDevices,
  isPendingSync,
  matchesSearch,
  readExpected,
  readSection,
  toFormValues,
  toRawValues,
} from './function';
import DeviceScheduleTable from './components/DeviceScheduleTable';
import PropertySelect from './components/PropertySelect';
import ScheduleEditModal from './components/ScheduleEditModal';
import { HomeIcon, RefreshIcon } from './components/Icons';
import './DeviceSchedule.css';

const cellKey = (deviceId, sectionKey) => `${deviceId}:${sectionKey}`;

function DeviceSchedule() {
  const housesList = useSelector((state) => state.housesList);
  const mutipleHouse = useSelector((state) => state.mutipleHouse);
  const login = useSelector((state) => state.login);

  const [houseId, setHouseId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  // Schedules asked for since this property was loaded, in the raw device shape,
  // keyed deviceId -> sectionKey. They belong on the `expected*` side: the save
  // succeeded, but the device has not reported the change back yet.
  const [overrides, setOverrides] = useState({});

  const mobile = login?.profile?.mobile?.slice(3);
  const houseOptions = useMemo(() => buildHouseOptions(housesList), [housesList]);

  const loadProperty = async (id, { force = false } = {}) => {
    if (!id || !mobile) return;
    if (!force && mutipleHouse[id]) return;
    setLoading(true);
    const res = await loadHouseForPicker(id, mobile);
    setLoading(false);
    if (res && res.success === false) {
      messages.error(res.message || 'Could not load this property');
    }
  };

  useEffect(() => {
    if (!houseOptions.length && mobile) fetchAllHouses(mobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile]);

  useEffect(() => {
    loadProperty(houseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseId]);

  const house = houseId ? mutipleHouse[houseId] : null;

  /**
   * Each row carries, per section, what the device is running (`current`), what
   * it has been asked for (`desired`) and whether the two disagree. The table
   * shows the first so it never claims a change the device has not made; the
   * editor opens on the second so a queued change can be seen and adjusted.
   */
  const allDevices = useMemo(() => {
    return collectEnergiSyncDevices(house).map((device) => ({
      ...device,
      sections: SCHEDULE_SECTIONS.reduce((acc, section) => {
        const current = readSection(device, section);
        const expected = { ...readExpected(device, section), ...(overrides[device.deviceId]?.[section.key] || {}) };
        const pending = isPendingSync(section, current, expected);

        acc[section.key] = {
          current: toFormValues(section, current),
          desired: toFormValues(section, pending ? { ...current, ...expected } : current),
          pending,
        };
        return acc;
      }, {}),
    }));
  }, [house, overrides]);

  const visibleDevices = useMemo(
    () => allDevices.filter((device) => matchesSearch(device, search)),
    [allDevices, search]
  );

  const roomCount = Array.isArray(house?.rooms) ? house.rooms.length : 0;

  const handleHouseChange = (nextId) => {
    setHouseId(nextId);
    setSearch('');
    setOverrides({});
    setEditTarget(null);
  };

  const handleRefresh = async () => {
    setOverrides({});
    await loadProperty(houseId, { force: true });
  };

  const handleEdit = (device, section) => {
    const state = device.sections[section.key];
    setEditTarget({ device, section, values: state.desired, pending: state.pending });
  };

  const handleSave = async (form) => {
    if (!editTarget) return;
    const { device, section } = editTarget;

    // Split back into the flat numbers the service takes.
    const raw = toRawValues(section, form);

    setSavingKey(cellKey(device.deviceId, section.key));
    const result = await updateDeviceSchedule({ deviceId: device.deviceId, section, values: raw });
    setSavingKey(null);

    if (!result.success) {
      messages.error(result.message || 'Could not update the schedule');
      return;
    }

    setOverrides((prev) => ({
      ...prev,
      [device.deviceId]: { ...(prev[device.deviceId] || {}), [section.key]: raw },
    }));
    setEditTarget(null);
    messages.success(result.message || `${section.label} sent to ${device.name}`);
  };

  return (
    <div className="deviceSchedulePage" style={{ width: '100%' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#3E3E3E', lineHeight: '28px' }}>Device Schedule</div>
        <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 3 }}>
          Review and update the schedule of every EnergiSync device on a property.
        </div>
      </div>

      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 12,
          padding: '15px 18px',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 11,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: theme.accentSoft,
                color: theme.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HomeIcon size={15} />
            </span>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.text, lineHeight: '17px' }}>Property</div>
              <div style={{ fontSize: 11.5, color: theme.textFaint, lineHeight: '16px' }}>
                {house
                  ? `${roomCount} rooms · ${allDevices.length} EnergiSync devices`
                  : 'Pick the property to work on'}
              </div>
            </div>
          </div>

          {houseId && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              title="Reload this property"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.textMuted,
                borderRadius: 8,
                padding: '6px 11px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: theme.font,
                cursor: loading ? 'progress' : 'pointer',
              }}
            >
              <span className={loading ? 'dsSpin' : undefined} style={{ display: 'flex' }}>
                <RefreshIcon size={13} />
              </span>
              Refresh
            </button>
          )}
        </div>

        <div style={{ maxWidth: 420 }}>
          <PropertySelect options={houseOptions} value={houseId} onChange={handleHouseChange} />
        </div>
      </div>

      <DeviceScheduleTable
        devices={visibleDevices}
        total={allDevices.length}
        loading={loading}
        hasHouse={!!houseId}
        search={search}
        onSearchChange={setSearch}
        onEdit={handleEdit}
        savingKey={savingKey}
      />

      <ScheduleEditModal
        open={!!editTarget}
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleSave}
        saving={!!savingKey}
      />
    </div>
  );
}

export default DeviceSchedule;
